/**
 * Market Context Provider
 * 
 * Manages opinion market state and provides on-chain data reading.
 * Supports permissionless markets - users can browse all markets and select one to interact with.
 * Integrates with backend API for market metadata and on-chain program for live data.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Connection, PublicKey, AccountInfo } from '@solana/web3.js';
import { Program, AnchorProvider, BN, BorshAccountsCoder } from '@coral-xyz/anchor';
import { solanaUtils } from '../utils/solanaUtils';
import { marketApiService } from '../services/marketApiService';
import { Market, MarketOutcome } from '../types/market';
import { useWallet } from './WalletProvider';
import { useMarketTransactions } from '../hooks/useMarketTransactions';
import OpinionMarketIDL from '../idl/opinion_market.json';

const OPINION_MARKET_PROGRAM_ID = new PublicKey(OpinionMarketIDL.address);

// On-chain market state (from contract)
export interface OnChainMarketState {
  marketId: number;
  creator: string;
  startTime: number;
  endTime: number;
  numOptions: number;
  totalDeposits: number; // in lamports
  totalParticipants: number;
  options: OnChainOptionState[];
  resolved: boolean; // Whether the market has been resolved on-chain (set on first sell after end_time)
  winner: number | null; // Winning option index (null if not yet resolved)
}

// On-chain option state
export interface OnChainOptionState {
  optionIndex: number;
  totalDeposits: number; // in lamports
  totalTokens: number;
  virtualLiquidity: number;
  percentage: number; // calculated
  isLeading: boolean; // calculated
}

// User's on-chain position
export interface OnChainPosition {
  user: string;
  marketId: number;
  optionIndex: number;
  tokens: number;
  costBasis: number; // in lamports - current amount at risk (not historical deposits)
}

// Combined market data (backend + on-chain)
export interface EnrichedMarket extends Market {
  onChainState?: OnChainMarketState;
  userPosition?: OnChainPosition;
  outcome?: MarketOutcome; // Finalized end-state from backend (for ended markets)
}

// User position summary across markets
export interface UserMarketPosition {
  market: EnrichedMarket;
  position: OnChainPosition;
  status: 'active' | 'ended_winner' | 'ended_loser' | 'claimed';
  canClaim: boolean;
  winningOptionIndex?: number;
}

// Pagination state
export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

interface MarketContextType {
  // MARKET LIST STATE
  markets: EnrichedMarket[];
  isLoadingMarkets: boolean;
  loadMarkets: (page?: number, status?: 'active' | 'closed') => Promise<void>;
  loadMoreMarkets: (status?: 'active' | 'closed') => Promise<void>;
  pagination: PaginationState;
  
  // SELECTED MARKET STATE
  selectedMarket: EnrichedMarket | null;
  isLoadingMarket: boolean;
  selectMarket: (address: string) => Promise<void>;
  clearSelectedMarket: () => void;
  
  // USER POSITIONS STATE
  userPositions: UserMarketPosition[];
  isLoadingPositions: boolean;
  refreshUserPositions: () => Promise<void>;
  
  // ERROR STATE
  error: string | null;
  
  // On-chain data refresh (for selected market)
  refreshMarketState: () => Promise<void>;
  refreshUserPosition: () => Promise<void>;
  
  // Helpers
  getMarketPDA: (marketId: number) => PublicKey;
  getPositionPDA: (marketPubkey: PublicKey, userWallet: PublicKey) => PublicKey;
  
  // Actions (operate on selectedMarket)
  buyTokens: (optionIndex: number, amountSol: number) => Promise<void>;
  sellTokens: (amountTokens: number, marketAddress?: string, marketCreator?: string) => Promise<void>;
}

const MarketContext = createContext<MarketContextType | undefined>(undefined);

/**
 * Convert a MarketOutcome (from backend DB) into an OnChainMarketState
 * so the rest of the app can use it the same way as live on-chain data.
 * This allows ended markets with snapshots to skip the Solana RPC call entirely.
 */
function outcomeToOnChainState(market: Market): OnChainMarketState | null {
  const outcome = market.outcome;
  if (!outcome || !outcome.options_snapshot) return null;

  const options: OnChainOptionState[] = outcome.options_snapshot.map((opt) => ({
    optionIndex: opt.optionIndex,
    totalDeposits: opt.totalDeposits,
    totalTokens: opt.totalTokens,
    virtualLiquidity: 0, // Not stored in outcome (not needed for ended markets)
    percentage: opt.percentage,
    isLeading: opt.isWinner
  }));

  return {
    marketId: 0, // Not stored in outcome, not needed for display
    creator: market.creator,
    startTime: Math.floor(new Date(market.starts_at).getTime() / 1000),
    endTime: Math.floor(new Date(market.ends_at).getTime() / 1000),
    numOptions: options.length,
    totalDeposits: outcome.total_volume_lamports,
    totalParticipants: outcome.total_participants,
    options,
    resolved: outcome.resolved,
    winner: outcome.winning_option_index
  };
}

export const MarketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { address, solanaWallet } = useWallet();
  
  // Initialize market transactions hook
  const {
    buyTokens: buyTokensOnChain,
    sellTokens: sellTokensOnChain,
    error: txError
  } = useMarketTransactions(solanaWallet);
  
  // Market list state
  const [markets, setMarkets] = useState<EnrichedMarket[]>([]);
  const [isLoadingMarkets, setIsLoadingMarkets] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 12, // 4x3 grid on desktop, more for mobile scrolling
    total: 0,
    hasMore: false
  });
  
  // Selected market state (when viewing a specific market)
  const [selectedMarket, setSelectedMarket] = useState<EnrichedMarket | null>(null);
  const [isLoadingMarket, setIsLoadingMarket] = useState(false);
  
  // User positions state
  const [userPositions, setUserPositions] = useState<UserMarketPosition[]>([]);
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);
  
  // Error state
  const [error, setError] = useState<string | null>(null);
  
  const connection = useMemo(
    () => new Connection(solanaUtils.getRpcEndpoint(), 'confirmed'),
    []
  );

  /**
   * Get Market PDA
   * Seeds: ["market", market_id (u64)]
   */
  const getMarketPDA = useCallback((marketId: number): PublicKey => {
    const marketIdBuffer = Buffer.alloc(8);
    marketIdBuffer.writeBigUInt64LE(BigInt(marketId));
    
    const [marketPDA] = PublicKey.findProgramAddressSync(
      [Buffer.from("market"), marketIdBuffer],
      OPINION_MARKET_PROGRAM_ID
    );
    
    return marketPDA;
  }, []);

  /**
   * Get Position PDA
   * Seeds: ["position", market_pubkey, user_pubkey]
   */
  const getPositionPDA = useCallback((marketPubkey: PublicKey, userWallet: PublicKey): PublicKey => {
    const [positionPDA] = PublicKey.findProgramAddressSync(
      [Buffer.from("position"), marketPubkey.toBuffer(), userWallet.toBuffer()],
      OPINION_MARKET_PROGRAM_ID
    );
    
    return positionPDA;
  }, []);

  /**
   * Get Protocol PDA
   * Seeds: ["protocol"]
   * 
   * Note: Currently unused but kept for future transaction building
   */
  // const getProtocolPDA = useCallback((): PublicKey => {
  //   const [protocolPDA] = PublicKey.findProgramAddressSync(
  //     [Buffer.from("protocol")],
  //     OPINION_MARKET_PROGRAM_ID
  //   );
  //   
  //   return protocolPDA;
  // }, []);

  /**
   * Fetch on-chain market state
   * 
   * @param marketAddress - The market PDA address (base58 string)
   */
  const fetchOnChainMarketState = useCallback(async (marketAddress: string): Promise<OnChainMarketState | null> => {
    // Guard: Skip if address is null/undefined/empty
    if (!marketAddress) {
      console.warn('Market address is null or empty, skipping');
      return null;
    }
    
    try {
      // Use the address directly - no need to derive PDA since we already have it
      const marketPDA = new PublicKey(marketAddress);
      
      // Create minimal provider for reading
      const provider = new AnchorProvider(
        connection,
        // @ts-ignore - minimal wallet adapter interface (read-only)
        {
          publicKey: PublicKey.default,
          signTransaction: async (tx: any) => tx,
          signAllTransactions: async (txs: any[]) => txs,
        },
        { commitment: 'confirmed' }
      );

      const program = new Program(OpinionMarketIDL as any, provider);
      
      // Fetch market account
      // @ts-ignore - Anchor types are dynamic based on IDL
      const marketAccount = await program.account.market.fetch(marketPDA);
      
      // IDL field names are snake_case, Anchor converts to camelCase
      // market_nonce -> marketNonce, total_deposits -> totalDeposits, etc.
      // Validate required fields exist (check both naming conventions)
      const marketNonce = marketAccount.marketNonce ?? marketAccount.market_nonce;
      const totalDepositsRaw = marketAccount.totalDeposits ?? marketAccount.total_deposits;
      
      if (!marketAccount || marketNonce === undefined || !totalDepositsRaw) {
        console.warn('Invalid market account data:', marketAddress, { marketNonce, totalDepositsRaw });
        return null;
      }
      
      // Get total deposits from market account
      const totalDeposits = (totalDepositsRaw as BN).toNumber();
      
      // Calculate percentages and determine leader
      const optionsArray = marketAccount.options as any[] || [];
      const options: OnChainOptionState[] = optionsArray.map((opt, idx) => {
        // Handle both naming conventions
        const optDeposits = opt?.totalDeposits ?? opt?.total_deposits;
        const optTokens = opt?.totalTokens ?? opt?.total_tokens;
        const optLiquidity = opt?.virtualLiquidity ?? opt?.virtual_liquidity;
        const optIndex = opt?.optionIndex ?? opt?.option_index ?? idx;
        
        const deposits = optDeposits?.toNumber?.() || 0;
        const percentage = totalDeposits > 0 ? (deposits / totalDeposits) * 100 : 0;
        
        return {
          optionIndex: optIndex,
          totalDeposits: deposits,
          totalTokens: optTokens?.toNumber?.() || 0,
          virtualLiquidity: optLiquidity?.toNumber?.() || 0,
          percentage,
          isLeading: false // will set below
        };
      });
      
      // Mark the leading option
      if (options.length > 0) {
        const maxDeposits = Math.max(...options.map(o => o.totalDeposits));
        options.forEach(opt => {
          opt.isLeading = opt.totalDeposits === maxDeposits && maxDeposits > 0;
        });
      }
      
      // Handle both naming conventions for all fields
      const startTimeRaw = marketAccount.startTime ?? marketAccount.start_time;
      const endTimeRaw = marketAccount.endTime ?? marketAccount.end_time;
      const numOptionsRaw = marketAccount.numOptions ?? marketAccount.num_options;
      const totalParticipantsRaw = marketAccount.totalParticipants ?? marketAccount.total_participants;
      
      // Parse resolution state (set on-chain when first sell happens after end_time)
      const resolved = marketAccount.resolved ?? false;
      const winnerRaw = marketAccount.winner ?? null;
      const winner = winnerRaw !== null && winnerRaw !== undefined ? winnerRaw as number : null;
      
      return {
        marketId: (marketNonce as BN).toNumber(),
        creator: (marketAccount.creator as PublicKey)?.toBase58?.() || '',
        startTime: (startTimeRaw as BN)?.toNumber?.() || 0,
        endTime: (endTimeRaw as BN)?.toNumber?.() || 0,
        numOptions: numOptionsRaw as number || 0,
        totalDeposits,
        totalParticipants: (totalParticipantsRaw as BN)?.toNumber?.() || 0,
        options,
        resolved,
        winner
      };
      
    } catch (err: any) {
      // If account doesn't exist yet, return null (not an error - silently handle)
      if (err.message?.includes('Account does not exist') || err.message?.includes('has no data')) {
        return null;
      }
      
      // Only log unexpected errors
      console.error('Failed to fetch on-chain market state:', err);
      throw err;
    }
  }, [connection]);

  /**
   * Batch fetch on-chain market states using getMultipleAccountsInfo
   * Much more efficient than individual fetches - fetches up to 100 accounts per RPC call
   * 
   * @param marketAddresses - Array of market PDA addresses (base58 strings)
   * @returns Map of address -> OnChainMarketState (null entries are excluded)
   */
  const batchFetchMarketStates = useCallback(async (
    marketAddresses: string[]
  ): Promise<Map<string, OnChainMarketState>> => {
    const results = new Map<string, OnChainMarketState>();
    
    if (marketAddresses.length === 0) {
      return results;
    }
    
    // Filter out invalid addresses
    const validAddresses = marketAddresses.filter(addr => addr && addr.length > 0);
    if (validAddresses.length === 0) {
      return results;
    }
    
    try {
      // Convert to PublicKeys
      const pubkeys = validAddresses.map(addr => new PublicKey(addr));
      
      // Create Anchor coder for decoding account data
      const coder = new BorshAccountsCoder(OpinionMarketIDL as any);
      
      // Fetch in batches of 100 (Solana limit for getMultipleAccountsInfo)
      const BATCH_SIZE = 100;
      const allAccountInfos: (AccountInfo<Buffer> | null)[] = [];
      
      for (let i = 0; i < pubkeys.length; i += BATCH_SIZE) {
        const batch = pubkeys.slice(i, i + BATCH_SIZE);
        const batchResults = await connection.getMultipleAccountsInfo(batch, 'confirmed');
        allAccountInfos.push(...batchResults);
      }
      
      // Process each account
      for (let i = 0; i < allAccountInfos.length; i++) {
        const accountInfo = allAccountInfos[i];
        const address = validAddresses[i];
        
        if (!accountInfo || !accountInfo.data) {
          // Account doesn't exist or has no data
          continue;
        }
        
        try {
          // Decode using Anchor coder (must match IDL account name exactly - 'Market' with capital M)
          const marketAccount = coder.decode('Market', accountInfo.data);
          
          // Parse the decoded data (same logic as fetchOnChainMarketState)
          const marketNonce = marketAccount.marketNonce ?? marketAccount.market_nonce;
          const totalDepositsRaw = marketAccount.totalDeposits ?? marketAccount.total_deposits;
          
          if (marketNonce === undefined || !totalDepositsRaw) {
            console.warn('Invalid market account data in batch:', address);
            continue;
          }
          
          const totalDeposits = (totalDepositsRaw as BN).toNumber();
          
          // Parse options
          const optionsArray = marketAccount.options as any[] || [];
          const options: OnChainOptionState[] = optionsArray.map((opt: any, idx: number) => {
            const optDeposits = opt?.totalDeposits ?? opt?.total_deposits;
            const optTokens = opt?.totalTokens ?? opt?.total_tokens;
            const optLiquidity = opt?.virtualLiquidity ?? opt?.virtual_liquidity;
            const optIndex = opt?.optionIndex ?? opt?.option_index ?? idx;
            
            const deposits = optDeposits?.toNumber?.() || 0;
            const percentage = totalDeposits > 0 ? (deposits / totalDeposits) * 100 : 0;
            
            return {
              optionIndex: optIndex,
              totalDeposits: deposits,
              totalTokens: optTokens?.toNumber?.() || 0,
              virtualLiquidity: optLiquidity?.toNumber?.() || 0,
              percentage,
              isLeading: false
            };
          });
          
          // Mark leading option
          if (options.length > 0) {
            const maxDeposits = Math.max(...options.map(o => o.totalDeposits));
            options.forEach(opt => {
              opt.isLeading = opt.totalDeposits === maxDeposits && maxDeposits > 0;
            });
          }
          
          // Parse timing and other fields
          const startTimeRaw = marketAccount.startTime ?? marketAccount.start_time;
          const endTimeRaw = marketAccount.endTime ?? marketAccount.end_time;
          const numOptionsRaw = marketAccount.numOptions ?? marketAccount.num_options;
          const totalParticipantsRaw = marketAccount.totalParticipants ?? marketAccount.total_participants;
          
          // Parse resolution state (set on-chain when first sell happens after end_time)
          const resolved = marketAccount.resolved ?? false;
          const winnerRaw = marketAccount.winner ?? null;
          const winner = winnerRaw !== null && winnerRaw !== undefined ? winnerRaw as number : null;
          
          const onChainState: OnChainMarketState = {
            marketId: (marketNonce as BN).toNumber(),
            creator: (marketAccount.creator as PublicKey)?.toBase58?.() || '',
            startTime: (startTimeRaw as BN)?.toNumber?.() || 0,
            endTime: (endTimeRaw as BN)?.toNumber?.() || 0,
            numOptions: numOptionsRaw as number || 0,
            totalDeposits,
            totalParticipants: (totalParticipantsRaw as BN)?.toNumber?.() || 0,
            options,
            resolved,
            winner
          };
          
          results.set(address, onChainState);
          
        } catch (decodeErr) {
          console.warn(`Failed to decode market account ${address}:`, decodeErr);
          continue;
        }
      }
      
      return results;
      
    } catch (err: any) {
      console.error('Failed to batch fetch market states:', err);
      // Return whatever we have so far (might be empty)
      return results;
    }
  }, [connection]);

  /**
   * Fetch user's on-chain position
   * 
   * @param marketAddress - The market PDA address (base58 string)
   * @param userWallet - The user's wallet public key
   */
  const fetchUserPosition = useCallback(async (marketAddress: string, userWallet: PublicKey): Promise<OnChainPosition | null> => {
    if (!marketAddress) {
      return null;
    }
    
    try {
      const marketPubkey = new PublicKey(marketAddress);
      const positionPDA = getPositionPDA(marketPubkey, userWallet);
      
      // Create minimal provider for reading
      const provider = new AnchorProvider(
        connection,
        // @ts-ignore - minimal wallet adapter interface (read-only)
        {
          publicKey: PublicKey.default,
          signTransaction: async (tx: any) => tx,
          signAllTransactions: async (txs: any[]) => txs,
        },
        { commitment: 'confirmed' }
      );

      const program = new Program(OpinionMarketIDL as any, provider);
      
      // Fetch position account
      // @ts-ignore - Anchor types are dynamic based on IDL
      const positionAccount = await program.account.position.fetch(positionPDA);
      
      // Handle both naming conventions (snake_case and camelCase)
      const optionIndex = positionAccount.optionIndex ?? positionAccount.option_index;
      const costBasis = positionAccount.costBasis ?? positionAccount.cost_basis;
      
      return {
        user: (positionAccount.user as PublicKey).toBase58(),
        marketId: 0, // Position doesn't store marketId, we know it from context
        optionIndex: optionIndex as number,
        tokens: (positionAccount.tokens as BN).toNumber(),
        costBasis: (costBasis as BN).toNumber()
      };
      
    } catch (err: any) {
      // If account doesn't exist, user has no position (not an error - silently handle)
      if (err.message?.includes('Account does not exist') || err.message?.includes('has no data')) {
        return null;
      }
      
      // Only log unexpected errors
      console.error('Failed to fetch user position:', err);
      throw err;
    }
  }, [connection, getPositionPDA]);

  /**
   * Refresh selected market state from on-chain
   * Skips RPC call for ended markets with outcome snapshots (state is final)
   */
  const refreshMarketState = useCallback(async () => {
    if (!selectedMarket) return;
    
    // Ended markets with outcome snapshots are finalized — no need to re-fetch
    if (selectedMarket.outcome) return;
    
    try {
      const onChainState = await fetchOnChainMarketState(selectedMarket.address);
      
      setSelectedMarket(prev => {
        if (!prev) return null;
        return {
          ...prev,
          onChainState: onChainState || undefined
        };
      });
    } catch (err: any) {
      console.error('Failed to refresh market state:', err);
      setError('Failed to refresh market data');
    }
  }, [selectedMarket, fetchOnChainMarketState]);

  /**
   * Refresh user position from on-chain (for selected market)
   */
  const refreshUserPosition = useCallback(async () => {
    if (!selectedMarket || !address || !selectedMarket.onChainState) return;
    
    try {
      const userWallet = new PublicKey(address);
      // Use the market address for position PDA derivation
      const position = await fetchUserPosition(selectedMarket.address, userWallet);
      
      setSelectedMarket(prev => {
        if (!prev) return null;
        return {
          ...prev,
          userPosition: position || undefined
        };
      });
    } catch (err: any) {
      console.error('Failed to refresh user position:', err);
      // Don't set error - position might not exist yet
    }
  }, [selectedMarket, address, fetchUserPosition]);

  /**
   * Refresh a specific market in the markets list with fresh on-chain data
   * Updates the market in the list without reloading all markets
   */
  const refreshMarketInList = useCallback(async (marketAddress: string) => {
    try {
      const onChainState = await fetchOnChainMarketState(marketAddress);
      
      if (!onChainState) return;
      
      // Update the market in the list
      setMarkets(prevMarkets => 
        prevMarkets.map(market => {
          if (market.address === marketAddress) {
            return {
              ...market,
              onChainState
            };
          }
          return market;
        })
      );
    } catch (err: any) {
      console.error('Failed to refresh market in list:', err);
      // Don't set error - this is a background refresh
    }
  }, [fetchOnChainMarketState]);

  /**
   * Load markets from backend and enrich with on-chain data
   * Uses batch fetching for efficiency (getMultipleAccountsInfo)
   * 
   * @param page - Page number to load (defaults to current page)
   * @param status - Market status filter: 'active' or 'closed' (defaults to 'active')
   */
  const loadMarkets = useCallback(async (page?: number, status: 'active' | 'closed' = 'active') => {
    setIsLoadingMarkets(true);
    setError(null);
    
    const targetPage = page ?? pagination.page;
    
    try {
      // 1. Fetch markets from backend
      // Higher limit to get all for client-side volume sorting
      const result = await marketApiService.getMarkets({
        page: targetPage,
        limit: 500, // Get all active/closed markets for proper sorting
        status: status
      });
      
      if (!result.success || !result.data) {
        setMarkets([]);
        setPagination(prev => ({ ...prev, page: targetPage, total: 0, hasMore: false }));
        setIsLoadingMarkets(false);
        return;
      }
      
      // Update pagination state from response
      if (result.pagination) {
        setPagination({
          page: result.pagination.page,
          limit: result.pagination.limit,
          total: result.pagination.total,
          hasMore: result.pagination.hasMore
        });
      }
      
      // 2. Filter out markets with null/empty addresses
      const validMarkets = result.data.filter(market => market.address && market.address.length > 0);
      
      if (validMarkets.length === 0) {
        setMarkets([]);
        setIsLoadingMarkets(false);
        return;
      }
      
      // 3. Split markets: those with outcome snapshots vs those needing on-chain fetch
      const marketsWithOutcome: Market[] = [];
      const marketsNeedingOnChain: Market[] = [];
      
      for (const market of validMarkets) {
        if (market.outcome) {
          marketsWithOutcome.push(market);
        } else {
          marketsNeedingOnChain.push(market);
        }
      }
      
      // 4. Batch fetch on-chain states only for markets without outcome snapshots
      const onChainStates = marketsNeedingOnChain.length > 0
        ? await batchFetchMarketStates(marketsNeedingOnChain.map(m => m.address))
        : new Map<string, OnChainMarketState>();
      
      // 5. Enrich markets with on-chain data (from RPC or outcome snapshot)
      const enrichedMarkets: EnrichedMarket[] = [];
      for (const market of validMarkets) {
        let onChainState: OnChainMarketState | undefined;
        
        if (market.outcome) {
          // Use backend snapshot for ended markets
          const fromOutcome = outcomeToOnChainState(market);
          onChainState = fromOutcome || undefined;
        } else {
          onChainState = onChainStates.get(market.address);
        }
        
        // Only include markets with valid state data
        if (onChainState) {
          enrichedMarkets.push({
            ...market,
            onChainState,
            outcome: market.outcome,
            userPosition: undefined // Position fetched when market is selected
          });
        } else {
          console.warn(`Market ${market.address} has no state data, filtering out`);
        }
      }
      
      setMarkets(enrichedMarkets);
      
    } catch (err: any) {
      console.error('Failed to load markets:', err);
      setError(err.message || 'Failed to load markets');
    } finally {
      setIsLoadingMarkets(false);
    }
  }, [pagination.page, batchFetchMarketStates]);

  /**
   * Load more markets (append to existing list)
   * Used for "Load More" button functionality
   * Uses batch fetching for efficiency
   * 
   * @param status - Market status filter: 'active' or 'closed' (defaults to 'active')
   */
  const loadMoreMarkets = useCallback(async (status: 'active' | 'closed' = 'active') => {
    if (!pagination.hasMore || isLoadingMarkets) return;
    
    setIsLoadingMarkets(true);
    setError(null);
    
    const nextPage = pagination.page + 1;
    
    try {
      // 1. Fetch next page of markets from backend
      const result = await marketApiService.getMarkets({
        page: nextPage,
        limit: 500, // Get all active/closed markets for proper sorting
        status: status
      });
      
      if (!result.success || !result.data) {
        setIsLoadingMarkets(false);
        return;
      }
      
      // Update pagination state from response
      if (result.pagination) {
        setPagination({
          page: result.pagination.page,
          limit: result.pagination.limit,
          total: result.pagination.total,
          hasMore: result.pagination.hasMore
        });
      }
      
      // 2. Filter out markets with null/empty addresses
      const validMarkets = result.data.filter(market => market.address && market.address.length > 0);
      
      if (validMarkets.length === 0) {
        setIsLoadingMarkets(false);
        return;
      }
      
      // 3. Split markets: those with outcome snapshots vs those needing on-chain fetch
      const marketsNeedingOnChain = validMarkets.filter(m => !m.outcome);
      
      // 4. Batch fetch on-chain states only for markets without outcome snapshots
      const onChainStates = marketsNeedingOnChain.length > 0
        ? await batchFetchMarketStates(marketsNeedingOnChain.map(m => m.address))
        : new Map<string, OnChainMarketState>();
      
      // 5. Enrich markets with on-chain data (from RPC or outcome snapshot)
      const enrichedMarkets: EnrichedMarket[] = [];
      for (const market of validMarkets) {
        let onChainState: OnChainMarketState | undefined;
        
        if (market.outcome) {
          const fromOutcome = outcomeToOnChainState(market);
          onChainState = fromOutcome || undefined;
        } else {
          onChainState = onChainStates.get(market.address);
        }
        
        if (onChainState) {
          enrichedMarkets.push({
            ...market,
            onChainState,
            outcome: market.outcome,
            userPosition: undefined
          });
        } else {
          console.warn(`Market ${market.address} has no state data, filtering out`);
        }
      }
      
      // 6. Append to existing markets (not replace)
      setMarkets(prev => [...prev, ...enrichedMarkets]);
      
    } catch (err: any) {
      console.error('Failed to load more markets:', err);
      setError(err.message || 'Failed to load more markets');
    } finally {
      setIsLoadingMarkets(false);
    }
  }, [pagination, isLoadingMarkets, batchFetchMarketStates]);

  /**
   * Select a specific market by address and load its full data
   */
  const selectMarket = useCallback(async (marketAddress: string) => {
    setIsLoadingMarket(true);
    setError(null);
    
    try {
      // 1. Fetch market metadata from backend
      const result = await marketApiService.getMarketByAddress(marketAddress);
      
      if (!result.success || !result.data) {
        setSelectedMarket(null);
        setIsLoadingMarket(false);
        setError('Market not found');
        return;
      }
      
      const market = result.data;
      
      // 2. Fetch on-chain state
      //    Optimization: if the backend returned outcome data (ended market with snapshot),
      //    convert it to OnChainMarketState and skip the Solana RPC call entirely.
      let onChainState: OnChainMarketState | undefined;
      
      if (market.outcome) {
        // Use backend snapshot -- no RPC call needed for ended markets
        const fromOutcome = outcomeToOnChainState(market);
        onChainState = fromOutcome || undefined;
      } else {
        // Active/upcoming market or no snapshot yet -- read live from chain
        try {
          const state = await fetchOnChainMarketState(market.address);
          onChainState = state || undefined;
        } catch (err) {
          console.warn('Market not yet created on-chain:', err);
          // Continue without on-chain state
        }
      }
      
      // 3. Fetch user position if wallet connected
      let userPosition: OnChainPosition | undefined;
      if (address && onChainState) {
        try {
          const userWallet = new PublicKey(address);
          // Use the market address for position PDA derivation
          const position = await fetchUserPosition(market.address, userWallet);
          userPosition = position || undefined;
        } catch (err) {
          console.warn('No user position found:', err);
          // Continue without position
        }
      }
      
      // 4. Combine all data
      setSelectedMarket({
        ...market,
        onChainState,
        userPosition,
        outcome: market.outcome
      });
      
    } catch (err: any) {
      console.error('Failed to load market:', err);
      setError(err.message || 'Failed to load market');
    } finally {
      setIsLoadingMarket(false);
    }
  }, [address, fetchOnChainMarketState, fetchUserPosition]);

  /**
   * Clear selected market (go back to list view)
   */
  const clearSelectedMarket = useCallback(() => {
    setSelectedMarket(null);
    setError(null);
  }, []);

  /**
   * Deposit SOL on a market option (wrapper for transaction + state refresh)
   */
  const buyTokens = useCallback(async (optionIndex: number, amountSol: number) => {
    if (!selectedMarket) {
      setError('No market selected');
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    try {
      const result = await buyTokensOnChain(
        selectedMarket.address,
        optionIndex,
        amountSol,
        selectedMarket.creator // Market creator for fee split
      );
      
      if (!result.success) {
        const errorMsg = result.error || 'Transaction failed';
        setError(errorMsg);
        return; // Don't throw, just return
      }
      
      // Refresh state after successful transaction
      await Promise.all([
        refreshMarketState(),
        refreshUserPosition(),
        refreshMarketInList(selectedMarket.address) // Also update market in list
      ]);
      
    } catch (err: any) {
      console.error('Failed to buy tokens:', err);
      const errorMsg = err.message || 'Failed to buy tokens';
      setError(errorMsg);
      // Don't throw - let the error state handle it
    }
  }, [selectedMarket, buyTokensOnChain, refreshMarketState, refreshUserPosition, refreshMarketInList]);

  /**
   * Fetch all user positions across all markets
   * Uses batch fetching for efficiency
   */
  const refreshUserPositions = useCallback(async () => {
    if (!address) {
      setUserPositions([]);
      return;
    }
    
    setIsLoadingPositions(true);
    
    try {
      const userWallet = new PublicKey(address);
      
      // 1. Fetch ALL markets from backend
      const marketsResult = await marketApiService.getAllMarkets();
      
      if (!marketsResult.success || !marketsResult.data) {
        console.warn('Failed to fetch markets:', marketsResult.error);
        setUserPositions([]);
        setIsLoadingPositions(false);
        return;
      }
      
      // Filter out markets with invalid addresses
      const validMarkets = marketsResult.data.filter(market => market.address && market.address.length > 0);
      
      if (validMarkets.length === 0) {
        setUserPositions([]);
        setIsLoadingPositions(false);
        return;
      }
      
      // 2. Batch fetch all market on-chain states
      const marketAddresses = validMarkets.map(m => m.address);
      const onChainStates = await batchFetchMarketStates(marketAddresses);
      
      // 3. Check each market for user position (still parallel for position checks)
      const positionChecks = validMarkets.map(async (market) => {
        try {
          // Get on-chain state from batch results
          const onChainState = onChainStates.get(market.address);
          if (!onChainState) return null;
          
          // Fetch user position using market address for PDA derivation
          const position = await fetchUserPosition(market.address, userWallet);
          if (!position || position.tokens === 0) return null;
          
          // Determine market status and if user can claim
          const now = Date.now();
          const endsAt = new Date(market.ends_at).getTime();
          const hasEnded = now >= endsAt;
          
          let status: UserMarketPosition['status'] = 'active';
          let canClaim = false;
          let winningOptionIndex: number | undefined;
          
          if (hasEnded) {
            // Determine winner: use on-chain winner if resolved, otherwise fall back to deposits
            if (onChainState.resolved && onChainState.winner !== null) {
              // On-chain winner is set (first sell after end_time has happened) - source of truth
              winningOptionIndex = onChainState.winner;
            } else {
              // Not yet resolved on-chain - preview winner based on current deposits
              const winningOption = onChainState.options.reduce((max, opt) => 
                opt.totalDeposits > max.totalDeposits ? opt : max
              );
              winningOptionIndex = winningOption.optionIndex;
            }
            
            const isWinner = position.optionIndex === winningOptionIndex;
            
            // Check if user has any tokens left (if 0, they've already sold/claimed)
            if (position.tokens === 0) {
              status = 'claimed';
              canClaim = false;
            } else if (isWinner) {
              status = 'ended_winner';
              canClaim = true;
            } else {
              status = 'ended_loser';
              canClaim = false;
            }
          }
          
          const enrichedMarket: EnrichedMarket = {
            ...market,
            onChainState,
            userPosition: position
          };
          
          return {
            market: enrichedMarket,
            position,
            status,
            canClaim,
            winningOptionIndex
          } as UserMarketPosition;
          
        } catch (err) {
          // Silently skip markets where we can't fetch data
          return null;
        }
      });
      
      // Wait for all checks to complete
      const results = await Promise.all(positionChecks);
      
      // Filter out nulls and sort by market end time (most recent first)
      const validPositions = results
        .filter((p): p is UserMarketPosition => p !== null)
        .sort((a, b) => {
          const aTime = new Date(a.market.ends_at).getTime();
          const bTime = new Date(b.market.ends_at).getTime();
          return bTime - aTime; // Most recent first
        });
      
      setUserPositions(validPositions);
      
    } catch (err: any) {
      console.error('Failed to refresh user positions:', err);
      setUserPositions([]);
    } finally {
      setIsLoadingPositions(false);
    }
  }, [address, batchFetchMarketStates, fetchUserPosition]);

  /**
   * Sell tokens from position (wrapper for transaction + state refresh)
   * 
   * @param amountTokens - Amount of tokens to sell
   * @param marketAddress - Optional market address (use when selectedMarket not available)
   * @param marketCreator - Optional market creator (use when selectedMarket not available)
   */
  const sellTokens = useCallback(async (
    amountTokens: number,
    marketAddress?: string,
    marketCreator?: string
  ) => {
    // Use provided params or fall back to selectedMarket
    const address = marketAddress || selectedMarket?.address;
    const creator = marketCreator || selectedMarket?.creator;
    
    if (!address || !creator) {
      setError('No market selected');
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    try {
      const result = await sellTokensOnChain(
        address,
        amountTokens,
        creator // Market creator for fee split
      );
      
      if (!result.success) {
        const errorMsg = result.error || 'Transaction failed';
        setError(errorMsg);
        return; // Don't throw, just return
      }
      
      // Refresh state after successful transaction
      const refreshPromises: Promise<void>[] = [
        refreshUserPositions(), // Always refresh all positions
      ];
      
      // Only refresh selected market state if we have one selected
      if (selectedMarket) {
        refreshPromises.push(
          refreshMarketState(),
          refreshUserPosition(),
          refreshMarketInList(selectedMarket.address)
        );
      } else {
        // Refresh the market in the list by address
        refreshPromises.push(refreshMarketInList(address));
      }
      
      await Promise.all(refreshPromises);
      
    } catch (err: any) {
      console.error('Failed to sell tokens:', err);
      const errorMsg = err.message || 'Failed to sell tokens';
      setError(errorMsg);
      // Don't throw - let the error state handle it
    }
  }, [selectedMarket, sellTokensOnChain, refreshMarketState, refreshUserPosition, refreshUserPositions, refreshMarketInList]);

  // NOTE: Markets are NOT loaded on mount anymore (lazy loading)
  // MarketList component calls loadMarkets() when it mounts
  // This avoids loading all markets when user navigates directly to /market/:address

  /**
   * Refresh user positions when wallet changes
   */
  useEffect(() => {
    if (address) {
      // Immediately clear old positions and set loading state
      setUserPositions([]);
      setIsLoadingPositions(true);
      
      // Fetch new positions
      refreshUserPositions();
    } else {
      // Clear positions when wallet disconnects
      setUserPositions([]);
      setIsLoadingPositions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]); // Only depend on address, not refreshUserPositions to avoid loops

  /**
   * Refresh user position when wallet connects/changes and market is selected
   */
  useEffect(() => {
    if (address && selectedMarket) {
      refreshUserPosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, selectedMarket?.address]); // Re-fetch position when wallet or market changes

  /**
   * Auto-refresh on-chain state every 30 seconds (for selected market)
   * Skip polling for ended markets that already have an outcome snapshot —
   * their state is finalized and won't change.
   */
  useEffect(() => {
    if (!selectedMarket?.onChainState) return;
    
    // Don't poll ended markets with outcome snapshots — state is final
    if (selectedMarket.outcome) return;
    
    const interval = setInterval(() => {
      refreshMarketState();
      if (address) {
        refreshUserPosition();
      }
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [selectedMarket?.onChainState, selectedMarket?.outcome, address, refreshMarketState, refreshUserPosition]);


  // Memoize context value
  // Note: We keep transaction loading separate from market loading
  // so UI can show button loading states instead of full-screen loading
  const contextValue = useMemo(() => ({
    // Market list
    markets,
    isLoadingMarkets,
    loadMarkets,
    loadMoreMarkets,
    pagination,
    
    // Selected market
    selectedMarket,
    isLoadingMarket,
    selectMarket,
    clearSelectedMarket,
    
    // User positions
    userPositions,
    isLoadingPositions,
    refreshUserPositions,
    
    // Error
    error: error || txError, // Show errors from both sources
    
    // On-chain refresh (for selected market)
    refreshMarketState,
    refreshUserPosition,
    
    // Helpers
    getMarketPDA,
    getPositionPDA,
    
    // Actions
    buyTokens,
    sellTokens,
  }), [
    markets,
    isLoadingMarkets,
    loadMarkets,
    loadMoreMarkets,
    pagination,
    selectedMarket,
    isLoadingMarket,
    selectMarket,
    clearSelectedMarket,
    userPositions,
    isLoadingPositions,
    refreshUserPositions,
    error,
    txError,
    refreshMarketState,
    refreshUserPosition,
    getMarketPDA,
    getPositionPDA,
    buyTokens,
    sellTokens,
  ]);

  return (
    <MarketContext.Provider value={contextValue}>
      {children}
    </MarketContext.Provider>
  );
};

/**
 * Hook to access market context
 */
export const useMarket = () => {
  const context = useContext(MarketContext);
  
  if (!context) {
    throw new Error('useMarket must be used within MarketProvider');
  }
  
  return context;
};
